import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { Stage, Layer, Image as KonvaImage, Rect, Text, Group, Transformer } from 'react-konva';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

export interface PdfRegion {
    id: string;
    pageNumber: number;
    xPercent: number;
    yPercent: number;
    widthPercent: number;
    heightPercent: number;
    label?: string;
    color?: string;
    isLocked?: boolean;
}

interface PdfKonvaViewerProps {
    pdfUrl: string | null;
    regions: PdfRegion[];
    interactive?: boolean;
    onRegionUpdate?: (region: PdfRegion) => void;
    onRegionClick?: (region: PdfRegion | null) => void;
    onRegionHover?: (region: PdfRegion | null, e: any) => void;
    selectedRegionId?: string | null;
}

export const PdfKonvaViewer: React.FC<PdfKonvaViewerProps> = ({
    pdfUrl,
    regions,
    interactive = false,
    onRegionUpdate,
    onRegionClick,
    onRegionHover,
    selectedRegionId
}) => {
    const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
    const [numPages, setNumPages] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    
    const [pdfImage, setPdfImage] = useState<HTMLCanvasElement | null>(null);
    const [pdfDimensions, setPdfDimensions] = useState({ width: 0, height: 0 });
    const [pdfError, setPdfError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    
    const containerRef = useRef<HTMLDivElement>(null);
    const [stageSize, setStageSize] = useState({ width: 800, height: 600 });
    const stageRef = useRef<any>(null);
    const trRef = useRef<any>(null);
    
    const [stageScale, setStageScale] = useState(1);
    const [stagePos, setStagePos] = useState({ x: 0, y: 0 });

    useEffect(() => {
        const updateSize = () => {
            if (containerRef.current) {
                setStageSize({
                    width: containerRef.current.offsetWidth,
                    height: containerRef.current.offsetHeight
                });
            }
        };
        const resizeObserver = new ResizeObserver(() => {
            requestAnimationFrame(updateSize);
        });
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }
        updateSize();
        return () => resizeObserver.disconnect();
    }, []);

    useEffect(() => {
        if (!pdfUrl) {
            setPdfDoc(null);
            setPdfImage(null);
            setPdfError(null);
            return;
        }
        let isMounted = true;
        setPdfError(null);
        setIsLoading(true);
        let loadingTask: pdfjsLib.PDFDocumentLoadingTask | null = null;
        
        const loadPdf = async () => {
            try {
                loadingTask = pdfjsLib.getDocument(pdfUrl);
                const pdf = await loadingTask.promise;
                if (!isMounted) return;
                setPdfDoc(pdf);
                setNumPages(pdf.numPages);
                setCurrentPage(1);
            } catch (err: any) {
                if (err.name === 'RenderingCancelledException' || err.name === 'PromiseCancelledException') return;
                console.error("Error loading PDF:", err);
                if (isMounted) setPdfError("Error al cargar el archivo PDF. Es posible que esté corrupto o el enlace sea inválido.");
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };
        loadPdf();
        return () => { 
            isMounted = false; 
            if (loadingTask) {
                try { loadingTask.destroy(); } catch (e) {}
            }
        };
    }, [pdfUrl]);

    useEffect(() => {
        if (!pdfDoc) return;
        let isMounted = true;
        let renderTask: pdfjsLib.RenderTask | null = null;
        
        const renderPage = async () => {
            setIsLoading(true);
            try {
                const page = await pdfDoc.getPage(currentPage);
                // Increase scale for better resolution on high DPI screens
                const viewport = page.getViewport({ scale: 2.5 }); 
                
                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');
                if (!context) return;
                
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                
                const renderContext = {
                    canvasContext: context,
                    viewport: viewport,
                    canvas: canvas
                };
                
                renderTask = page.render(renderContext);
                await renderTask.promise;
                if (!isMounted) return;
                
                setPdfImage(canvas);
                setPdfDimensions({ width: viewport.width, height: viewport.height });
                
                if (containerRef.current && stageSize.width > 0) {
                    const cw = stageSize.width;
                    const ch = stageSize.height;
                    const scale = Math.min(cw / viewport.width, ch / viewport.height) * 0.95;
                    setStageScale(scale);
                    setStagePos({
                        x: (cw - viewport.width * scale) / 2,
                        y: (ch - viewport.height * scale) / 2
                    });
                }
            } catch (err: any) {
                if (err.name === 'RenderingCancelledException') return;
                console.error("Error rendering page:", err);
                if (isMounted) setPdfError("Error al renderizar la página.");
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };
        renderPage();
        return () => { 
            isMounted = false; 
            if (renderTask) {
                try { renderTask.cancel(); } catch (e) {}
            }
        };
    }, [pdfDoc, currentPage, stageSize.width, stageSize.height]);

    const handleWheel = (e: any) => {
        e.evt.preventDefault();
        const scaleBy = 1.1;
        const stage = e.target.getStage();
        if (!stage) return;
        const oldScale = stage.scaleX();
        const pointer = stage.getPointerPosition();
        if (!pointer) return;
        
        const mousePointTo = {
            x: pointer.x / oldScale - stage.x() / oldScale,
            y: pointer.y / oldScale - stage.y() / oldScale,
        };

        const newScale = e.evt.deltaY < 0 ? oldScale * scaleBy : oldScale / scaleBy;
        
        if (newScale < 0.05 || newScale > 20) return;
        
        setStageScale(newScale);
        setStagePos({
            x: -(mousePointTo.x - pointer.x / newScale) * newScale,
            y: -(mousePointTo.y - pointer.y / newScale) * newScale,
        });
    };

    // Touch events for pinch-to-zoom
    const lastDistRef = useRef<number>(0);
    const lastCenterRef = useRef<{x: number, y: number} | null>(null);

    const getDistance = (p1: any, p2: any) => Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
    const getCenter = (p1: any, p2: any) => ({ x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 });

    const handleTouchMove = (e: any) => {
        e.evt.preventDefault();
        const touch1 = e.evt.touches[0];
        const touch2 = e.evt.touches[1];

        if (touch1 && touch2) {
            const stage = e.target.getStage();
            if (!stage) return;
            if (stage.isDragging()) stage.stopDrag();

            const p1 = { x: touch1.clientX, y: touch1.clientY };
            const p2 = { x: touch2.clientX, y: touch2.clientY };

            const dist = getDistance(p1, p2);
            const center = getCenter(p1, p2);

            if (!lastDistRef.current) lastDistRef.current = dist;

            const scaleBy = dist / lastDistRef.current;
            const oldScale = stage.scaleX();
            let newScale = oldScale * scaleBy;
            
            if (newScale < 0.05) newScale = 0.05;
            if (newScale > 20) newScale = 20;

            const stageRect = stage.container().getBoundingClientRect();
            const pointer = {
                x: center.x - stageRect.left,
                y: center.y - stageRect.top,
            };

            const mousePointTo = {
                x: pointer.x / oldScale - stage.x() / oldScale,
                y: pointer.y / oldScale - stage.y() / oldScale,
            };

            setStageScale(newScale);
            setStagePos({
                x: -(mousePointTo.x - pointer.x / newScale) * newScale,
                y: -(mousePointTo.y - pointer.y / newScale) * newScale,
            });

            lastDistRef.current = dist;
            lastCenterRef.current = center;
        }
    };

    const handleTouchEnd = () => {
        lastDistRef.current = 0;
        lastCenterRef.current = null;
    };

    // Keyboard fine movements
    useEffect(() => {
        if (!interactive || !selectedRegionId) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            const activeRegion = regions.find(r => r.id === selectedRegionId);
            if (!activeRegion || activeRegion.isLocked) return;

            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                e.preventDefault();
                
                // Base move step. For shift, make it 10x larger
                const step = e.shiftKey ? 0.5 : 0.05; 
                let newX = activeRegion.xPercent;
                let newY = activeRegion.yPercent;

                if (e.key === 'ArrowUp') newY -= step;
                if (e.key === 'ArrowDown') newY += step;
                if (e.key === 'ArrowLeft') newX -= step;
                if (e.key === 'ArrowRight') newX += step;

                if (onRegionUpdate) {
                    onRegionUpdate({
                        ...activeRegion,
                        xPercent: newX,
                        yPercent: newY
                    });
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [interactive, selectedRegionId, regions, onRegionUpdate]);

    useEffect(() => {
        if (interactive && selectedRegionId && trRef.current && stageRef.current) {
            const node = stageRef.current.findOne(`#region-${selectedRegionId}`);
            const region = regions.find(r => r.id === selectedRegionId);
            
            if (node && region && !region.isLocked) {
                trRef.current.nodes([node]);
                trRef.current.getLayer().batchDraw();
            } else {
                trRef.current.nodes([]);
            }
        } else if (trRef.current) {
            trRef.current.nodes([]);
        }
    }, [selectedRegionId, interactive, regions, currentPage]);

    const handleDragEnd = (e: any, region: PdfRegion) => {
        if (!interactive || !onRegionUpdate) return;
        const node = e.target;
        const newX = node.x();
        const newY = node.y();
        
        onRegionUpdate({
            ...region,
            xPercent: (newX / pdfDimensions.width) * 100,
            yPercent: (newY / pdfDimensions.height) * 100
        });
    };

    const handleTransformEnd = (e: any, region: PdfRegion) => {
        if (!interactive || !onRegionUpdate) return;
        const node = e.target;
        const scaleX = node.scaleX();
        const scaleY = node.scaleY();

        node.scaleX(1);
        node.scaleY(1);

        onRegionUpdate({
            ...region,
            xPercent: (node.x() / pdfDimensions.width) * 100,
            yPercent: (node.y() / pdfDimensions.height) * 100,
            widthPercent: ((node.width() * scaleX) / pdfDimensions.width) * 100,
            heightPercent: ((node.height() * scaleY) / pdfDimensions.height) * 100
        });
    };

    const activeRegions = regions.filter(r => r.pageNumber === currentPage);

    return (
        <div className="flex flex-col h-full bg-slate-100 dark:bg-slate-950 overflow-hidden relative" ref={containerRef}>
            {pdfUrl && numPages > 0 && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 bg-white dark:bg-slate-800 shadow-lg rounded-full px-4 py-2 flex items-center gap-4 border border-slate-200 dark:border-slate-700">
                    <button 
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage <= 1}
                        className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full disabled:opacity-50"
                    >
                        <span className="material-symbols-outlined text-slate-600 dark:text-slate-300">chevron_left</span>
                    </button>
                    <span className="text-sm font-bold text-slate-700 dark:text-slate-200">
                        Pág. {currentPage} de {numPages}
                    </span>
                    <button 
                        onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
                        disabled={currentPage >= numPages}
                        className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full disabled:opacity-50"
                    >
                        <span className="material-symbols-outlined text-slate-600 dark:text-slate-300">chevron_right</span>
                    </button>
                </div>
            )}
            
            {!pdfUrl && !pdfError && !isLoading && (
                <div className="flex items-center justify-center h-full text-slate-400">
                    <p>No hay plano seleccionado</p>
                </div>
            )}
            
            {pdfError && (
                <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/90 dark:bg-slate-900/90 text-red-500 gap-2 p-6 text-center">
                    <span className="material-symbols-outlined text-4xl">error</span>
                    <p className="font-bold">{pdfError}</p>
                </div>
            )}
            
            {isLoading && !pdfImage && (
                <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/50 dark:bg-slate-900/50 text-blue-500 gap-2">
                    <span className="material-symbols-outlined text-4xl animate-spin">refresh</span>
                    <p className="font-bold">Cargando PDF...</p>
                </div>
            )}

            {pdfUrl && stageSize.width > 0 && (
                <Stage
                    width={stageSize.width}
                    height={stageSize.height}
                    onWheel={handleWheel}
                    scaleX={stageScale}
                    scaleY={stageScale}
                    x={stagePos.x}
                    y={stagePos.y}
                    draggable
                    ref={stageRef}
                    onMouseDown={(e) => {
                        const clickedEmpty = e.target === e.target.getStage() || e.target.hasName('pdf-background');
                        if (clickedEmpty) {
                            if (onRegionClick) onRegionClick(null);
                        }
                    }}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    onTouchStart={(e) => {
                        const clickedEmpty = e.target === e.target.getStage() || e.target.hasName('pdf-background');
                        if (clickedEmpty) {
                            if (onRegionClick) onRegionClick(null);
                        }
                    }}
                >
                    <Layer>
                        {pdfImage && (
                            <KonvaImage
                                name="pdf-background"
                                image={pdfImage}
                                width={pdfDimensions.width}
                                height={pdfDimensions.height}
                            />
                        )}
                        {activeRegions.map(region => {
                            const x = (region.xPercent / 100) * pdfDimensions.width;
                            const y = (region.yPercent / 100) * pdfDimensions.height;
                            const w = (region.widthPercent / 100) * pdfDimensions.width;
                            const h = (region.heightPercent / 100) * pdfDimensions.height;
                            
                            const isSelected = selectedRegionId === region.id;
                            
                            return (
                                <Group
                                    key={region.id}
                                    id={`region-${region.id}`}
                                    x={x}
                                    y={y}
                                    width={w}
                                    height={h}
                                    draggable={interactive && isSelected && !region.isLocked}
                                    onDragEnd={(e) => handleDragEnd(e, region)}
                                    onTransformEnd={(e) => handleTransformEnd(e, region)}
                                    onClick={() => onRegionClick?.(region)}
                                    onTap={() => onRegionClick?.(region)}
                                    onMouseEnter={(e) => {
                                        const stage = e.target.getStage();
                                        if (stage) stage.container().style.cursor = interactive ? 'pointer' : 'default';
                                        if (onRegionHover) onRegionHover(region, e);
                                    }}
                                    onMouseLeave={(e) => {
                                        const stage = e.target.getStage();
                                        if (stage) stage.container().style.cursor = 'default';
                                        if (onRegionHover) onRegionHover(null, e);
                                    }}
                                >
                                    <Rect
                                        width={w}
                                        height={h}
                                        fill={region.color || 'rgba(156, 163, 175, 0.4)'}
                                        stroke={isSelected ? '#3b82f6' : (region.color ? 'transparent' : '#4b5563')}
                                        strokeWidth={isSelected ? 4 / stageScale : 2 / stageScale}
                                        opacity={0.8}
                                        cornerRadius={4 / stageScale}
                                    />
                                    {region.label && (
                                        <Text
                                            text={region.label}
                                            fill="#111827"
                                            fontSize={Math.max(12 / stageScale, 8)}
                                            fontStyle="bold"
                                            align="center"
                                            verticalAlign="middle"
                                            width={w}
                                            height={h}
                                            padding={2 / stageScale}
                                            shadowColor="white"
                                            shadowBlur={4}
                                            shadowOpacity={0.9}
                                        />
                                    )}
                                </Group>
                            );
                        })}
                        {interactive && (
                            <Transformer 
                                ref={trRef} 
                                rotateEnabled={false}
                                keepRatio={false}
                                boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 1 || newBox.height < 1) return oldBox;
                                    return newBox;
                                }} 
                            />
                        )}
                    </Layer>
                </Stage>
            )}
        </div>
    );
};
